import React, { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { debugZodResolver } from '@/lib/debugZodResolver';
import { CampdeskLogo } from '@/components/svg/CampdeskLogo.tsx';
import AuthFormContainer from '@/features/auth/signup/components/AuthFormContainer.tsx';
import { TextInput } from '@/components/form/TextInput.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase.ts';
import { useAuth } from '@/contexts/AuthContext.tsx';
import useSignOut from '@/features/auth/login/hooks/useSignOut.ts';
import AppLoader from '@/features/dashboard/_root/components/AppLoader.tsx';
import useAcceptInvitation from '@/features/public/acceptInvitation/hooks/useAcceptInvitation.ts';
import {
  acceptInvitationSchema,
  AcceptInvitationFormData,
} from '@/features/public/acceptInvitation/schemas/acceptInvitationSchema.ts';
import { acceptInvitationRoute } from '@/features/public/acceptInvitation/routes/acceptInvitationRoute.ts';
import {
  isInvitationExpired,
  readInvitationTokenPayload,
} from '@/features/public/acceptInvitation/utils/invitationTokenPayload.ts';
import { getMeQueryOptions } from '@/features/dashboard/_root/hooks/useMe.ts';
import { storeCompanyId } from '@/features/dashboard/_root/access/currentCompany.ts';
import { queryClient } from '@/App.tsx';
import { isKnownBackendErrorKey } from '@/lib/api/exception.ts';

interface InvitationScreenProps {
  title: string;
  footer: ReactNode;
  children: ReactNode;
}

const InvitationScreen = ({
  title,
  footer,
  children,
}: InvitationScreenProps) => (
  <div className="flex min-h-screen w-full items-center justify-center bg-basic-white p-10 max-md:p-8 max-sm:p-6">
    <div className="flex w-full max-w-[520px] flex-col items-center gap-10">
      <CampdeskLogo />
      <AuthFormContainer title={title}>
        {children}
        {footer}
      </AuthFormContainer>
    </div>
  </div>
);

interface InvitationNoticeProps {
  title: string;
  description: ReactNode;
  footer: ReactNode;
  children?: ReactNode;
}

const InvitationNotice = ({
  title,
  description,
  footer,
  children,
}: InvitationNoticeProps) => (
  <InvitationScreen title={title} footer={footer}>
    <div className="flex w-full flex-col items-start gap-8">
      <p className="text-sm text-[#6C6C93]">{description}</p>
      {children}
    </div>
  </InvitationScreen>
);

const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const { token } = acceptInvitationRoute.useSearch();
  const { mutate: acceptInvitation, isPending } = useAcceptInvitation();
  const { clearAppState } = useSignOut();
  const { data: me, isPending: meLoading } = useQuery(getMeQueryOptions(user));
  const [invalidReason, setInvalidReason] = useState<
    'expired' | 'notFound' | null
  >(null);
  // Chwila otwarcia strony zamiast `Date.now()` w renderze, który musi być czysty.
  const [openedAt] = useState(() => new Date());

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInvitationFormData>({
    resolver: debugZodResolver(acceptInvitationSchema),
  });

  useEffect(() => {
    if (authLoading || !user) return;
    if (!user.emailVerified) {
      // Backend odrzuca akceptację bez zweryfikowanego maila, a stąd nie było dokąd pójść.
      navigate({
        to: '/auth/email-verification',
        search: {
          redirect: `${window.location.pathname}${window.location.search}`,
        },
      });
    }
  }, [authLoading, user, navigate]);

  if (authLoading) {
    return <AppLoader />;
  }

  const invitation = readInvitationTokenPayload(token);

  if (!user) {
    const redirect = `${window.location.pathname}${window.location.search}`;
    return (
      <InvitationNotice
        title={
          invitation.companyName
            ? `Zaproszenie do firmy ${invitation.companyName}`
            : 'Zaproszenie do zespołu'
        }
        description={
          invitation.email
            ? `Zaproszenie jest dla adresu ${invitation.email}. Zaloguj się albo załóż konto tym adresem, aby dołączyć.`
            : 'Zaloguj się albo załóż konto, aby dołączyć.'
        }
        footer={null}
      >
        <div className="flex w-full flex-col gap-4">
          <ActionButton
            tone="accent"
            size="block"
            onClick={() =>
              navigate({ to: '/auth/login', search: { redirect } })
            }
          >
            Zaloguj się
          </ActionButton>
          <ActionButton
            tone="outline"
            size="block"
            onClick={() =>
              navigate({ to: '/auth/signup', search: { redirect } })
            }
          >
            Załóż konto
          </ActionButton>
        </div>
      </InvitationNotice>
    );
  }

  if (!user.emailVerified || meLoading) {
    return <AppLoader />;
  }

  // Stary link otwarty przez członka firmy: formularz i tak skończyłby się błędem z backendu.
  const joined = me?.accesses.find(
    (access) => access.companyId === invitation.companyId,
  );
  const emailMismatch =
    invitation.email !== null &&
    invitation.email.toLowerCase() !== user.email?.toLowerCase();
  const expired =
    invalidReason === 'expired' || isInvitationExpired(invitation, openedAt);

  // Nie `useSignOut().signOut`: ten gubi link zaproszenia, a po wylogowaniu
  // ta sama strona pokaże logowanie i rejestrację z `redirect` na to zaproszenie.
  const handleSignOut = async () => {
    await signOut(auth);
    clearAppState();
  };

  const onSubmit = (data: AcceptInvitationFormData) => {
    acceptInvitation(
      { token, firstName: data.firstName, lastName: data.lastName },
      {
        onError: (error) => {
          if (isKnownBackendErrorKey(error, 'INVITATION_EXPIRED')) {
            setInvalidReason('expired');
          } else if (isKnownBackendErrorKey(error, 'INVITATION_NOT_FOUND')) {
            setInvalidReason('notFound');
          }
        },
      },
    );
  };

  const signedInAs = (
    <div className="flex w-full flex-col items-center gap-3 border-t border-[#F0F0F5] pt-6">
      <p className="text-sm text-[#6C6C93]">
        Zalogowano jako{' '}
        <span className="font-medium text-[#2E2E38]">{user.email}</span>
      </p>
      <ActionButton tone="outline" size="block" onClick={handleSignOut}>
        Wyloguj się
      </ActionButton>
    </div>
  );

  if (joined) {
    const enter = () => {
      storeCompanyId(joined.companyId);
      queryClient.clear();
      navigate({ to: '/app' });
    };
    return (
      <InvitationNotice
        title="Jesteś już w zespole"
        description={
          <>
            Należysz już do firmy{' '}
            <span className="font-medium text-[#2E2E38]">
              {joined.companyName}
            </span>
            .
          </>
        }
        footer={signedInAs}
      >
        <ActionButton tone="accent" size="block" onClick={enter}>
          Wejdź
        </ActionButton>
      </InvitationNotice>
    );
  }

  if (emailMismatch) {
    return (
      <InvitationNotice
        title="Zaproszenie dla innego adresu"
        description={`To zaproszenie jest dla adresu ${invitation.email}. Wyloguj się i zaloguj tym adresem, aby je przyjąć.`}
        footer={signedInAs}
      />
    );
  }

  if (expired) {
    return (
      <InvitationNotice
        title="Zaproszenie wygasło"
        description="Poproś osobę, która Cię zaprosiła, o nowe zaproszenie."
        footer={signedInAs}
      />
    );
  }

  if (invalidReason === 'notFound') {
    return (
      <InvitationNotice
        title="Zaproszenie jest nieaktualne"
        description="Zaproszenie zostało wycofane albo już wykorzystane. Poproś o nowe, jeśli nadal masz dołączyć."
        footer={signedInAs}
      />
    );
  }

  return (
    <InvitationScreen title="Dołącz do zespołu" footer={signedInAs}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col items-start gap-8"
      >
        <div className="w-full space-y-4">
          <TextInput
            label="Imię"
            register={register('firstName')}
            placeholder="Wpisz imię"
            required={true}
            error={errors.firstName?.message}
          />
          <TextInput
            label="Nazwisko"
            register={register('lastName')}
            placeholder="Wpisz nazwisko"
            required={true}
            error={errors.lastName?.message}
          />
        </div>
        <ActionButton
          tone="accent"
          size="block"
          type="submit"
          isLoading={isPending}
        >
          Dołącz
        </ActionButton>
      </form>
    </InvitationScreen>
  );
};

export default AcceptInvitationPage;
